import { useCallback, useMemo, useRef, useState } from 'react'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import TableLink from './TableLink'
import SidebarDragChip from './SidebarDragChip'
import { ROOT_FLAT_TABLE_LABEL_LEFT } from '../utils/rowGeometry'
import {
  SIDEBAR_DRAG_MEASURING,
  listDragCollision,
  sidebarDragAnnouncements,
  sidebarListDragModifiers,
} from '../logic/dragInList'
import type { TableSidebarItem } from '../api/contracts'

interface FlatTableListProps {
  /** Tables in the order and filter to show. */
  tables: readonly TableSidebarItem[]
  /** Custom sort without a search: rows can be dragged into a new order. */
  reorderEnabled: boolean
  onReorder: (activeTableId: string, overTableId: string) => void
  /** A drag started; the drawer stays open until `onDragFinish`. */
  onDragBegin?: () => void
  onDragFinish?: () => void
}

/** The Tables sidebar with folders off: one list, reorderable under Custom. */
export default function FlatTableList({
  tables,
  reorderEnabled,
  onReorder,
  onDragBegin,
  onDragFinish,
}: FlatTableListProps) {
  const [activeTable, setActiveTable] = useState<TableSidebarItem | null>(null)
  const navRef = useRef<HTMLElement | null>(null)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const collisionDetection = useMemo(() => listDragCollision(), [])
  const dragModifiers = useMemo(() => sidebarListDragModifiers(() => navRef.current), [])

  const describeDragId = useCallback(
    (id: UniqueIdentifier) =>
      tables.find((table) => `table-${table.id}` === String(id))?.display_name_plural ?? 'a table',
    [tables]
  )
  const accessibility = useMemo(
    () => ({ announcements: sidebarDragAnnouncements(describeDragId) }),
    [describeDragId]
  )

  const finish = () => {
    setActiveTable(null)
    onDragFinish?.()
  }

  const handleDragStart = (event: DragStartEvent) => {
    const tableId = event.active.data.current?.tableId as string | undefined
    setActiveTable(tables.find((table) => table.id === tableId) ?? null)
    onDragBegin?.()
  }

  const handleDragEnd = (event: DragEndEvent) => {
    finish()
    const activeTableId = event.active.data.current?.tableId as string | undefined
    const overTableId = event.over?.data.current?.tableId as string | undefined
    if (activeTableId && overTableId && activeTableId !== overTableId) {
      onReorder(activeTableId, overTableId)
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      modifiers={dragModifiers}
      measuring={SIDEBAR_DRAG_MEASURING}
      accessibility={accessibility}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={finish}
    >
      <nav
        ref={navRef}
        className="w-full min-w-0 space-y-[var(--sidebar-item-inset)]"
        data-tour="sidebar-tables"
      >
        <SortableContext
          items={tables.map((table) => `table-${table.id}`)}
          strategy={verticalListSortingStrategy}
        >
          {tables.map((table) => (
            <TableLink
              key={table.id}
              table={table}
              labelInset={ROOT_FLAT_TABLE_LABEL_LEFT}
              sortableId={reorderEnabled ? `table-${table.id}` : undefined}
            />
          ))}
        </SortableContext>
      </nav>
      <DragOverlay dropAnimation={null} modifiers={dragModifiers}>
        {activeTable ? (
          <SidebarDragChip kind="table" label={activeTable.display_name_plural} />
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}
